import React, { useEffect, useState, useCallback } from "react";
import { LazyLevelProps } from "../types";
import { Level } from "./Level";
import { Box, CircularProgress, Typography, Button } from "@mui/material";

export function LazyLevel<T>({
    load,
    deps = [],
    preloadOn = "click",
    loadingElement,
    errorElement,
    ...levelProps
}: LazyLevelProps<T>) {
    const [options, setOptions] = useState<T[] | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);

    const loadData = useCallback(async () => {
        if (isLoading) return;

        setIsLoading(true);
        setError(null);

        try {
            const data = await load();
            setOptions(data);
            setHasLoaded(true);
        } catch (err) {
            setError(err instanceof Error ? err : new Error("Failed to load"));
        } finally {
            setIsLoading(false);
        }
    }, [load, isLoading]);

    // Reload when dependencies change (but only if already loaded once)
    useEffect(() => {
        if (hasLoaded) {
            loadData();
        }
    }, deps);

    // Initial load based on preloadOn strategy
    useEffect(() => {
        if (preloadOn === "click" && !hasLoaded) {
            // Load immediately when LazyLevel is rendered (which means parent was clicked/expanded)
            loadData();
        }
    }, [preloadOn, hasLoaded, loadData]);

    // Loading state
    if (isLoading && !options) {
        return (
            <Box sx={{ py: 2, px: 2, display: "flex", alignItems: "center", gap: 1 }}>
                {loadingElement || (
                    <>
                        <CircularProgress size={16} />
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Loading...
                        </Typography>
                    </>
                )}
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ py: 2, px: 2 }}>
                {errorElement ? (
                    errorElement(error, loadData)
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Typography variant="body2" color="error">
                            {error.message}
                        </Typography>
                        <Button size="small" onClick={loadData}>
                            Retry
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }

    // Render Level with loaded options
    return <Level<T> {...levelProps} options={options} />;
}

export default LazyLevel;
